import { createSlice } from "@reduxjs/toolkit";
import {
  getPatients,
  postPatient,
  deletePatient,
  updatePatient,
  getPatientsCities,
  getPatientShippingAddresses,
  postPatientShippingAddress,
  patchPatientShippingAddress,
  getPatient,
  getPatientTopProducts,
  getPatientAlpineiqTimeline,
  getPatientAlpineiqWallet,
} from "./patientActions";
import { parsePatientCreateResult } from "./patientCreateResult";

const initialState = {
  loading: false,
  error: null,
  success: false,
  items: [],
  patientsCities: [],
  citiesLoading: false,
  shipping_addresses: {},
  patientTopProducts: [],
  loadingTopProducts: false,
  alpineiqWallet: null,
  loadingAlpineiqWallet: false,
  alpineiqTimeline: null,
  loadingAlpineiqTimeline: false,
};

const patientSlice = createSlice({
  name: "patients",
  initialState,
  reducers: {
    resetError: (state, action) => {
      if (action?.payload) {
        delete state.error[action?.payload];
        if (Object.keys(state.error)?.length === 0) {
          state.error = null;
        }
      } else {
        state.error = null;
      }
      state.loading = false;
      state.success = false;
    },
    resetPatientTopProducts: (state, action) => {
      state.patientTopProducts = action?.payload || [];
    },
    resetAlpineiqWallet: (state) => {
      state.alpineiqWallet = null;
      state.loadingAlpineiqWallet = false;
      state.alpineiqTimeline = null;
      state.loadingAlpineiqTimeline = false;
    },
    resetPatientSuccess: (state) => {
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPatients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPatients.fulfilled, (state, { payload }) => {
        state.items = payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(getPatients.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(postPatient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(postPatient.fulfilled, (state, { payload }) => {
        state.loading = false;
        // A phone already on file answers with that existing customer
        // (already_exists): move its row to the top instead of adding a
        // second one.
        const { patient } = parsePatientCreateResult(payload);
        const others = (state?.items?.results || []).filter(
          (el) => el?.id !== patient?.id
        );
        state.items = {
          meta: state?.items?.meta,
          results: [patient, ...others],
        };
        state.error = null;
        state.success = true;
      })
      .addCase(postPatient.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(updatePatient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePatient.fulfilled, (state, { payload }) => {
        const { id } = payload;
        const updatedPatients = state.items?.results?.map((el) => {
          if (el?.id === id) {
            return payload;
          } else {
            return el;
          }
        });
        const newObj = {
          meta: state.items?.meta,
          results: updatedPatients,
        };
        state.items = newObj;
        state.loading = false;
        state.error = false;
        state.success = true;
      })
      .addCase(updatePatient.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(deletePatient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePatient.fulfilled, (state, { payload }) => {
        const { id } = payload;
        const updatedPatients = state.items?.results.filter((el) => el.id !== id);
        const newObj = {
          meta: state.items?.meta,
          results: updatedPatients,
        };
        state.items = newObj;
        state.loading = false;
        state.error = false;
      })
      .addCase(deletePatient.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(getPatientsCities.pending, (state) => {
        state.citiesLoading = true;
        state.error = null;
      })
      .addCase(getPatientsCities.fulfilled, (state, { payload }) => {
        state.patientsCities = payload;
        state.citiesLoading = false;
        state.error = null;
      })
      .addCase(getPatientsCities.rejected, (state, { payload }) => {
        state.citiesLoading = false;
        state.error = payload;
      })
      .addCase(getPatientShippingAddresses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPatientShippingAddresses.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.shipping_addresses = payload;
        state.error = null;
      })
      .addCase(getPatientShippingAddresses.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(postPatientShippingAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(postPatientShippingAddress.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.shipping_addresses = {
          meta: state.shipping_addresses?.meta,
          results: [payload, ...state?.shipping_addresses?.results],
        };
        state.error = null;
      })
      .addCase(postPatientShippingAddress.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(patchPatientShippingAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(patchPatientShippingAddress.fulfilled, (state, { payload }) => {
        const updatedAddresses = state?.shipping_addresses?.results?.map((el) => {
          if (el?.id === payload?.id) {
            return payload;
          } else {
            return el;
          }
        });
        const newObj = {
          meta: state?.shipping_addresses?.meta,
          results: updatedAddresses,
        };
        state.shipping_addresses = newObj;
        state.loading = false;
        state.error = false;
      })
      .addCase(patchPatientShippingAddress.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(getPatient.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.items = [];
      })
      .addCase(getPatient.fulfilled, (state, { payload }) => {
        state.items = payload
          ? {
              results: [payload],
              meta: {
                count: 1,
              },
            }
          : [];
        state.loading = false;
        state.error = null;
      })
      .addCase(getPatient.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(getPatientTopProducts.pending, (state) => {
        state.loadingTopProducts = true;
      })
      .addCase(getPatientTopProducts.fulfilled, (state, { payload }) => {
        state.loadingTopProducts = false;
        state.patientTopProducts = payload;
        state.error = null;
      })
      .addCase(getPatientTopProducts.rejected, (state, { payload }) => {
        state.loadingTopProducts = false;
        state.error = payload;
      })
      .addCase(getPatientAlpineiqWallet.pending, (state) => {
        state.loadingAlpineiqWallet = true;
      })
      .addCase(getPatientAlpineiqWallet.fulfilled, (state, { payload }) => {
        state.loadingAlpineiqWallet = false;
        state.alpineiqWallet = payload;
      })
      .addCase(getPatientAlpineiqTimeline.pending, (state) => {
        state.loadingAlpineiqTimeline = true;
      })
      .addCase(getPatientAlpineiqTimeline.fulfilled, (state, { payload }) => {
        state.loadingAlpineiqTimeline = false;
        state.alpineiqTimeline = payload;
      })
      .addCase(getPatientAlpineiqTimeline.rejected, (state) => {
        // Same contract as the wallet: a 400 means the org has AlpineIQ
        // off, so the panel simply doesn't render the history block.
        state.loadingAlpineiqTimeline = false;
        state.alpineiqTimeline = null;
      })
      .addCase(getPatientAlpineiqWallet.rejected, (state) => {
        // A 400 here means "org hasn't enabled AlpineIQ" — a normal state,
        // so the panel just hides; never surface it as a global error.
        state.loadingAlpineiqWallet = false;
        state.alpineiqWallet = null;
      });
  },
});
export const {
  resetError,
  resetPatientTopProducts,
  resetPatientSuccess,
  resetAlpineiqWallet,
} = patientSlice.actions;
export default patientSlice.reducer;
